import apiClient from './apiClient';

const BASE_PATH = 'v1/tag-categories';

export interface CreateTagCategoryRequest {
  name: string;
  description: string;
  isActive: boolean;
}

export interface TagCategoryDTO {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface CreateTagRequest {
  categoryId: string;
  fieldValue: string;
  isActive: boolean;
}

export interface TagDataDTO {
  id: string;
  categoryId: string;
  fieldValue: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateLookupRequest {
  categoryName: string;
  description?: string;
  active?: boolean;
  values: {
    id?: string;
    valueName: string;
    description?: string;
    orderNumber?: number;
    active?: boolean;
    deleteFlag?: boolean;
  }[];
}

export interface LookupDTO {
  id: string;
  categoryName: string;
  description: string;
  active: boolean;
  lookupValues: {
    id: string;
    valueName: string;
    description: string;
    orderNumber?: number;
    createdAt?: string;
    updatedAt?: string;
  }[];
  createdAt?: string;
  updatedAt?: string;
}

class MasterDataService {
  async createTagCategory(request: CreateTagCategoryRequest): Promise<TagCategoryDTO> {
    const response = await apiClient.post<TagCategoryDTO>(BASE_PATH, request);
    return response.data;
  }

  async updateTagCategory(id: string, request: CreateTagCategoryRequest): Promise<TagCategoryDTO> {
    const response = await apiClient.put<TagCategoryDTO>(`${BASE_PATH}/${id}`, request);
    return response.data;
  }

  async deleteTagCategory(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  }

  async getAllTagCategories(): Promise<TagCategoryDTO[]> {
    const response = await apiClient.get(BASE_PATH);
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async searchTagCategories(name?: string, status?: string): Promise<TagCategoryDTO[]> {
    const params: Record<string, string> = {};
    if (name) params.name = name;
    if (status && status !== 'All') params.status = status;

    if (Object.keys(params).length === 0) {
      return this.getAllTagCategories();
    }

    const response = await apiClient.get(`${BASE_PATH}/search`, { params });
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async createTag(request: CreateTagRequest): Promise<TagDataDTO> {
    const response = await apiClient.post<TagDataDTO>('v1/tags', request);
    return response.data;
  }

  async updateTag(id: string, request: CreateTagRequest): Promise<TagDataDTO> {
    const response = await apiClient.put<TagDataDTO>(`v1/tags/${id}`, request);
    return response.data;
  }

  async deleteTag(id: string): Promise<void> {
    await apiClient.delete(`v1/tags/${id}`);
  }

  async getAllTags(): Promise<TagDataDTO[]> {
    const response = await apiClient.get(`v1/tags`);
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async searchTags(tagName?: string, value?: string, status?: string): Promise<TagDataDTO[]> {
    const params: Record<string, string> = {};
    if (tagName) params.tagName = tagName;
    if (value) params.value = value;
    if (status && status !== 'All') params.status = status;

    if (Object.keys(params).length === 0) {
      return this.getAllTags();
    }

    const response = await apiClient.get(`v1/tags/search`, { params });
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async getTagsByCategory(categoryId: string): Promise<TagDataDTO[]> {
    const response = await apiClient.get(`v1/tags/category/${categoryId}`);
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async getTagsByCategoryName(categoryName: string): Promise<TagDataDTO[]> {
    const response = await apiClient.get(`v1/tags/category-name/${categoryName}`);
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async createLookup(request: CreateLookupRequest): Promise<LookupDTO> {
    const response = await apiClient.post<LookupDTO>('v1/lookups', request);
    return response.data;
  }

  async updateLookup(id: string, request: CreateLookupRequest): Promise<LookupDTO> {
    const response = await apiClient.put<LookupDTO>(`v1/lookups/${id}`, request);
    return response.data;
  }

  async deleteLookup(id: string): Promise<void> {
    await apiClient.delete(`v1/lookups/${id}`);
  }

  async deleteLookupValue(id: string): Promise<void> {
    await apiClient.delete(`v1/lookups/values/${id}`);
  }

  async getAllLookups(): Promise<LookupDTO[]> {
    const response = await apiClient.get('v1/lookups');
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async getActiveLookupValues(id: string): Promise<{ id: string; valueName: string; description: string; orderNumber: number; active: boolean }[]> {
    const response = await apiClient.get(`v1/lookups/${id}/active`);
    let arr = [];
    if (Array.isArray(response.data)) {
      arr = response.data;
    } else if (response.data && Array.isArray(response.data.content)) {
      arr = response.data.content;
    } else if (response.data && Array.isArray(response.data.data)) {
      arr = response.data.data;
    } else if (response.data && Array.isArray(response.data.lookupValues)) {
      arr = response.data.lookupValues;
    }
    return arr.sort((a: any, b: any) => (a.orderNumber || 0) - (b.orderNumber || 0));
  }

  async getActiveLookupValuesByName(categoryName: string): Promise<{ id: string; valueName: string; description: string; orderNumber: number; active: boolean }[]> {
    const response = await apiClient.get(`v1/lookups/name/${categoryName}/active`);
    let arr = [];
    if (Array.isArray(response.data)) {
      arr = response.data;
    } else if (response.data && Array.isArray(response.data.content)) {
      arr = response.data.content;
    } else if (response.data && Array.isArray(response.data.data)) {
      arr = response.data.data;
    } else if (response.data && Array.isArray(response.data.lookupValues)) {
      arr = response.data.lookupValues;
    }
    return arr.sort((a: any, b: any) => (a.orderNumber || 0) - (b.orderNumber || 0));
  }

  async searchLookups(name: string, status: boolean = true): Promise<LookupDTO[]> {
    const response = await apiClient.get(`v1/lookups/search`, {
      params: { name, status }
    });
    if (response.data && Array.isArray(response.data.content)) {
      return response.data.content;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  async getLookupById(id: string): Promise<LookupDTO> {
    const response = await apiClient.get(`v1/lookups/${id}`);
    return response.data;
  }

  async getActiveLookupById(id: string): Promise<LookupDTO> {
    const response = await apiClient.get(`v1/lookups/${id}/active`);
    return response.data;
  }

  async downloadLookupTemplate(): Promise<any> {
    return await apiClient.get('v1/lookups/template', {
      responseType: 'blob'
    });
  }

  async downloadLookupsData(): Promise<any> {
    return await apiClient.get('v1/lookups/download', {
      responseType: 'blob'
    });
  }

  async uploadLookups(file: File): Promise<{ id?: string; uploadId?: string; fileId?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('v1/lookups/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async confirmLookups(fileId: string, file: File): Promise<{ totalRows: number; successCount: number; errorCount: number; errorMessages?: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`v1/lookups/files/confirm/${fileId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async downloadLookupErrorFile(fileId: string): Promise<any> {
    return await apiClient.get(`v1/lookups/files/error/download/${fileId}`, {
      responseType: 'blob'
    });
  }

  async appendCorrectedLookups(fileId: string, file: File): Promise<{ id?: string; uploadId?: string; fileId?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`v1/lookups/files/append/${fileId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async getRecentUploads(): Promise<any[]> {
    const response = await apiClient.get('v1/lookups/files/latest');
    return response.data;
  }
}

export default new MasterDataService();
